import { Injectable, NgZone } from '@angular/core';
import { GoogleAuthProvider } from 'firebase/auth';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Storage } from '@ionic/storage-angular';
import { FirebaseService } from './firebase.service';
import moment from 'moment';
import { UserJourneyService } from './user-journey.service';
import { UserService } from './user.service';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import firebase from 'firebase/compat';
import UserCredential = firebase.auth.UserCredential;

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  user: any;
  userStatus: boolean = false;
  public userRole: string;
  private timeout: any;
  private idelTime: number = 5 * 60 * 1000;    // 5 minutes
  private tokenRefreshInterval: any;

  constructor(
    private storage: Storage,
    private firebaseService: FirebaseService,
    private userJourneyService: UserJourneyService,
    public afAuth: AngularFireAuth,
    private ngZone: NgZone,
    private userService: UserService,
    private http: HttpClient,
    private httpClient: HttpClient,
  ) {
    this.storage.create();

    /* Saving user data in localstorage when
    logged in and setting up null when logged out */
    this.afAuth.authState.subscribe(async (user) => {
      if (user && user?.emailVerified && await this.isApprovedUser(user.uid)) {
        // this.userJourneyService.activeDefaultModule(user.uid).then();
        this.userJourneyService.listenAndUpdateUserJourney(user.uid).then();
        this.userJourneyService.setSessionLastLogin(user.uid).then();
        this.setUserRole(user.uid).then();
        if (!this.user) {
          this.updateUserFromDB(user);
        }

        this.setUserOnlineStatus(true);
      } else {
        // User logged out or token expired
        console.log('Auth state changed - user not authenticated');
        this.userRole = '';
        this.setUserOnlineStatus(false);
        this.userJourneyService.closeUserJourneySubscription();

        // Clear storage if user was previously logged in but auth state is now null
        const storedUser = await this.storage.get('user');
        if (storedUser && !user) {
          console.warn('User was logged in but auth state is null - clearing session');
          await this.storage.clear();
        }
      }

      window.addEventListener('beforeunload', () => {
        this.setUserOnlineStatus(false);
      });

      this.startInactivityCheck();
    });

    // Start token refresh timer to prevent token expiration
    this.startTokenRefreshTimer();
  }

  private setUserOnlineStatus(status: boolean = true) {
    if (this.user && this.userStatus !== status) {
      this.userStatus = status;
      this.firebaseService.updateDocument('/users', this.user.uid, { online: status });
    }
  }

  private startInactivityCheck() {
    document.addEventListener('mousemove', () => this.resetTimer(), true);
    document.addEventListener('keypress', () => this.resetTimer(), true);

    this.resetTimer();
  }

  private resetTimer() {
    clearTimeout(this.timeout);
    this.setUserOnlineStatus(true);

    this.ngZone.runOutsideAngular(() => {
      this.timeout = setTimeout(() => {
        this.ngZone.run(() => {
          this.setUserOnlineStatus(false);
        });
      }, this.idelTime);
    });
  }

  private startTokenRefreshTimer(): void {
    // Clear any existing interval
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }

    // Refresh token every 50 minutes (Firebase tokens expire after 1 hour)
    this.tokenRefreshInterval = setInterval(async () => {
      const user = await this.afAuth.currentUser;
      if (user) {
        try {
          await user.getIdToken(true); // Force refresh
          console.log('Firebase token refreshed successfully');
        } catch (error) {
          console.error('Token refresh failed:', error);
          // If refresh fails, log out the user
          console.warn('Logging out user due to token refresh failure');
          await this.logout();
        }
      }
    }, 50 * 60 * 1000); // 50 minutes
  }

  // Get user from DB and save data to storage
  updateUserFromDB(user) {
    // Get user from DB to store locally
    this.firebaseService.getDocument(`/users/${user.uid}`).then(async (u: any) => {

      if (!u) return;   // first time login, the user does not exist in the DB
      this.firebaseService.updateDocument('/users', user.uid, { last_login: new Date().getTime() }).then(async _ => {

        u.uid = user.uid;
        this.user = u;
        await this.storage.set('user', this.user);
      })
    })
  }

  // Sign in email and password
  public EmailAuth = async (data) => {
    const res = await this.afAuth.signInWithEmailAndPassword(data.email, data.password);
    return this.completeLogin(res);
  };

  public loginByToken = async (token: string) => {
    const res = await this.afAuth.signInWithCustomToken(token);
    return this.completeLogin(res);
  };

  public completeLogin = async (userCredential: UserCredential) => {
    let user;
    const approvedUser = await this.isUserAuthorized(userCredential.user);
    if (approvedUser) {
      user = {
        ...userCredential.user.providerData[0],
        uid: userCredential.user.uid,
        approved: approvedUser.approved,
        emailVerified: userCredential.user.emailVerified || approvedUser.noEmailVerificationNeeded,
      };
    } else {
      user = {
        ...userCredential.user.providerData[0],
        uid: userCredential.user.uid,
        approved: false,
        emailVerified: userCredential.user.emailVerified
      };
    }
    return user;
  };


  // Sign in with Google
  GoogleAuth() {
    return this.afAuth.signInWithPopup(new GoogleAuthProvider()).then(async res => {
      let user;
      let approvedUser = await this.isUserAuthorized(res.user);
      if (approvedUser) {
        user = {
          ...res.user.providerData[0],
          uid: res.user.uid,
          approved: approvedUser.approved,
        }
      }
      // No value in 'authorized_users' = the user logs-in with goole for the first time
      else {
        // Autorize the user
        this.firebaseService.updateDocument('/authorized_users', res.user.uid, true);
        user = {
          ...res.user.providerData[0],
          uid: res.user.uid,
          approved: true,
        }
      }

      return user;

    })
      .catch((error) => {
        console.error(error);
      });
  }

  Register(value) {
    return new Promise<any>((resolve, reject) => {
      this.afAuth.createUserWithEmailAndPassword(value.email, value.password).then(res => resolve(res), err => reject(err));
    });
  }

  async SendVerificationMail() {
    return this.afAuth.currentUser
      .then((u: any) => u.sendEmailVerification())
      .then(() => {
      });
  }

  ForgotPassword(passwordResetEmail: string) {
    return this.afAuth.sendPasswordResetEmail(passwordResetEmail).then(() => {
      return { success: true };
    })
      .catch((error) => {
        let message;
        if (error.code == "auth/user-not-found") message = "The email address was not found.";
        if (error.code == "auth/invalid-email") message = "Invalid email";
        return { success: false, message };
      });
  }

  isUserAuthorized(user) {
    return new Promise<any>((resolve, reject) => {
      this.firebaseService.getDocument('/authorized_users/' + user.uid).then(res => {
          resolve(res);
        },
        err => {
          reject(err);
        });
    });
  }

  async logout(callback?: () => Promise<any>, reload = true) {
    // Clear token refresh interval
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }

    await this.storage.clear();
    await this.afAuth.signOut();
    if (callback) {
      await callback();
    }
    if (localStorage.getItem('auth_flow') === 'PROVIDER_LOGIN') {
      this.logoutByProvider();
    }
    if (reload) {
      location.reload();
    }
  }

  async getUserJourney(company, uid) {
    return this.firebaseService.getDocument(`user_journey/${company}/${uid}`).then((journey: any) => {
      if (journey) {
        if (journey.demo && (moment(journey.demo) < moment())) {
          return false;
        } else {
          this.userJourneyService.listenAndUpdateUserJourney(uid);
          return true;
        }
      } else return true
    });
  }

  async getUser() {
    return this.storage.get('user').then((user) => {
      if (!user) return null;
      return user;
    });
  }

  public loginWithApiToken = async (email: string, idToken: string): Promise<any> => {
    const { token } = await lastValueFrom(this.http.post<any>(`${environment.apiUrl}/auth/login/token`, { email, idToken }),);
    const authUser = (await this.afAuth.signInWithCustomToken(token)).user;
    if (!authUser) {
      throw new Error('auth/user-not-found');
    }
    const approvedUser = await this.isUserAuthorized(authUser);

    return {
      ...authUser.providerData[0],
      uid: authUser.uid,
      approved: approvedUser?.approved,
      emailVerified: authUser.emailVerified,
    };
  };

  public getCurrentAuthData = async (forceRefresh: boolean = false): Promise<{ email: string; idToken: string }> => {
    const currentUser = await this.afAuth.currentUser;
    return {
      email: currentUser!.email!,
      idToken: await currentUser!.getIdToken(forceRefresh),
    };
  };

  async isConnected() {
    const user = await this.storage.get('user');
    return !!user;
  }

  private async isApprovedUser(uid: string): Promise<boolean> {
    return !!(await this.firebaseService.getDocument(`authorized_users/${uid}/approved`));
  }

  public async isApprovedCurrentUser(): Promise<boolean> {
    try {
      const user = await this.storage.get('user');
      if(!user) {
        return false;
      }
      return !!(await this.firebaseService.getDocument(`authorized_users/${user?.uid}/approved`));
    } catch {
      return false;
    }
  }

  private setUserRole = async (uid: string): Promise<void> => {
    this.userRole = await this.userService.getUserProperty(uid, 'role');
  };

  public async getAuthorizedUser(uid: string): Promise<any> {
    try {
      return await this.firebaseService.getDocument(`/authorized_users/${uid}`);
    } catch (error) {
      console.error('Error fetching authorized user:', error);
      throw error;
    }
  };

  public async authorizeUser(user: any): Promise<boolean> {
    try {
      await this.firebaseService.updateDocument('/authorized_users', user.uid, {
        email: user.email,
        approved: true
      });
      user.approved = true;
      return true;
    } catch (error) {
      console.error('Error authorizing user:', error);
      throw error;
    }
  }

  public generateRandomString = (length: number = 64): string => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    const randomValues = new Uint32Array(length);
    window.crypto.getRandomValues(randomValues);
    for (let i = 0; i < length; i++) {
      result += charset[randomValues[i] % charset.length];
    }
    return result;
  };

  public generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(new Uint8Array(digest));
  };

  private base64UrlEncode = (bytes: Uint8Array): string => {
    return btoa(String.fromCharCode(...Array.from(bytes)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  public loginByProvider = (body: any): Observable<any> => {
    const url = environment.apiUrl + '/auth/login/provider';
    return this.httpClient.post(url, body, { responseType: 'text' });
  };

  public logoutByProvider = (): void => {
    const clientId = environment.aws.clientId;
    const logoutUri = encodeURIComponent(environment.aws.logoutUri);
    const cognitoDomain = environment.aws.cognitoDomain;

    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
  };

  public redirectToProviderSignIn = async (provider: string): Promise<void> => {
    const cognitoDomain = environment.aws.cognitoDomain;
    const clientId = environment.aws.clientId;
    const redirectUri = encodeURIComponent(environment.aws.redirectUri);

    const state = this.generateRandomString(32);
    const codeVerifier = this.generateRandomString();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    localStorage.setItem(`pkce_v:${state}`, codeVerifier);

    const nonce = this.generateRandomString();
    localStorage.setItem(`pkce_n:${state}`, nonce);

    localStorage.setItem('sso_provider', provider);

    window.location.href =
      `${cognitoDomain}/oauth2/authorize` +
      `?response_type=code` +
      `&client_id=${clientId}` +
      `&redirect_uri=${redirectUri}` +
      `&identity_provider=${provider}` +
      `&scope=openid+email` +
      `&code_challenge_method=S256` +
      `&code_challenge=${codeChallenge}` +
      `&state=${state}` +
      `&nonce=${nonce}`;
  };
}
